import { createReducer, on } from '@ngrx/store';
import {
  accountPageActions,
  accountPageAPIActions,
  accountSearchAPIActions,
  accountSearchPageActions,
  accountUsagePageActions,
  accountUsagePageAPIActions,
  studentsPageActions,
  studentsPageAPIActions,
  teacherApiActions,
  teacherPageActions
} from './accounts.actions';
import { initialState, AccountsState } from './accounts.state';
import { Sort } from '@angular/material/sort';
import { Subscription } from '../services/models/subscriptions.model';

export const accountReducer = createReducer(
  initialState,

  on(accountSearchPageActions.userResetAccountSearch, (state) => ({
    ...state,
    accountLookup: {
      ...initialState.accountLookup
    }
  })),

  on(accountSearchPageActions.userWantsArchivedAccounts, (state: AccountsState, { showAnonymizedAccounts }) => {
    return {
      ...state,
      accountLookup: {
        ...state.accountLookup,
        loading: true,
        accountSearchForm: {
          ...state.accountLookup.accountSearchForm,
          showAnonymizedAccounts: showAnonymizedAccounts
        }
      }
    };
  }),

  on(accountSearchPageActions.userSubmittedAccountSearch, (state: AccountsState, { searchTerm, selectedProperty }) => ({
    ...state,
    accountLookup: {
      ...initialState.accountLookup,
      loading: true,
      accountSearchForm: {
        ...state.accountLookup.accountSearchForm,
        searchTerm: searchTerm,
        selectedProperty: selectedProperty
      }
    }
  })),

  on(accountSearchAPIActions.searchAccountsSuccess, (state: AccountsState, { searchResults }) => {
    return {
      ...state,
      accountLookup: {
        ...state.accountLookup,
        searchResponse: searchResults.pageData,
        loading: false,
        error: null,
        moreAccountsToLoad: searchResults.moreToCome
      }
    };
  }),

  on(accountSearchAPIActions.searchAccountsFailure, (state: AccountsState, { error }) => ({
    ...state,
    accountLookup: {
      ...state.accountLookup,
      error: error,
      searchResponse: [],
      loading: false,
      moreAccountsToLoad: false
    }
  })),

  on(
    accountSearchPageActions.userClickedLoadMoreAccounts,
    (state: AccountsState, { searchTerm, selectedProperty }) => ({
      ...state,
      accountLookup: {
        ...state.accountLookup,
        accountSearchForm: {
          ...state.accountLookup.accountSearchForm,
          pageSize: state.accountLookup.accountSearchForm.pageSize + 12,
          searchTerm: searchTerm,
          selectedProperty: selectedProperty
        }
      }
    })
  ),

  on(accountPageActions.initAccountPage, (state: AccountsState, { accountId }) => ({
    ...state,
    selectedAccount: {
      ...initialState.selectedAccount,
      loading: true,
      accountId: accountId
    }
  })),

  on(accountPageAPIActions.loadAccountSuccess, (state: AccountsState, { account }) => ({
    ...state,
    selectedAccount: {
      ...state.selectedAccount,
      loading: false,
      accountId: account.accountId,
      accountDetails: {
        ...account
      }
    }
  })),

  on(accountPageAPIActions.loadAccountSubscriptionSuccess, (state, { subscriptions, licenceSummaries }) => ({
    ...state,
    selectedAccount: {
      ...state.selectedAccount,
      accountSubscriptions: {
        ...state.selectedAccount.accountSubscriptions,
        loading: false,
        sort: state.selectedAccount.accountSubscriptions.sort,
        licenceSummaries: licenceSummaries,
        subscriptions: subscriptions,
        filteredSubscriptions: sortSubscriptions(
          state.selectedAccount.accountSubscriptions.filter
            ? (subscriptions?.filter(
                (subscription) =>
                  !state.selectedAccount.accountSubscriptions.filter ||
                  subscription.subscriptionStatusId === state.selectedAccount.accountSubscriptions.filter
              ) ?? [])
            : subscriptions,
          state.selectedAccount.accountSubscriptions.sort
        )
      }
    }
  })),

  on(
    (accountPageAPIActions.addSubscriptionSuccess,
    accountPageAPIActions.editSubscriptionSuccess,
    accountPageAPIActions.addSiteLicenceSuccess,
    accountPageAPIActions.addFreeTrialSuccess,
    accountPageAPIActions.addLiteracyOfferSuccess),
    (state) => ({
      ...state,
      selectedAccount: {
        ...state.selectedAccount,
        accountSubscriptions: {
          ...state.selectedAccount.accountSubscriptions,
          loading: true,
          sort: initialState.selectedAccount.accountSubscriptions.sort,
          siteLicences: [],
          subscriptions: [],
          filteredSubscriptions: []
        }
      }
    })
  ),

  on(accountPageActions.userUpdatedSubscriptionFilter, (state, { filter }) => ({
    ...state,
    selectedAccount: {
      ...state.selectedAccount,
      accountSubscriptions: {
        ...state.selectedAccount.accountSubscriptions,
        filter: filter,
        sort: initialState.selectedAccount.accountSubscriptions.sort,
        filteredSubscriptions: state.selectedAccount.accountSubscriptions.subscriptions.filter(
          (subscription) => !filter || subscription.subscriptionStatusId === filter
        )
      }
    }
  })),

  on(accountPageActions.userUpdatedSubscriptionTableSort, (state, { sort }) => ({
    ...state,
    selectedAccount: {
      ...state.selectedAccount,
      accountSubscriptions: {
        ...state.selectedAccount.accountSubscriptions,
        sort: sort,
        filteredSubscriptions: sortSubscriptions(
          [...state.selectedAccount.accountSubscriptions.filteredSubscriptions],
          sort
        )
      }
    }
  })),

  on(accountPageAPIActions.loadAccountNotesSuccess, (state, { notes }) => ({
    ...state,
    selectedAccount: {
      ...state.selectedAccount,
      accountNotes: {
        ...state.selectedAccount.accountNotes,
        loading: false,
        notes: notes,
        filter: '',
        filteredNotes: notes
      }
    }
  })),

  on(accountPageActions.userUpdatedNotesFilter, (state, { filter }) => {
    const allNotes = state.selectedAccount.accountNotes.notes;
    const filteredNotes = !filter || filter === '' ? allNotes : allNotes.filter((note) => note.type === filter);

    return {
      ...state,
      selectedAccount: {
        ...state.selectedAccount,
        accountNotes: {
          ...state.selectedAccount.accountNotes,
          filter,
          filteredNotes
        }
      }
    };
  }),

  on(accountPageActions.userClickedExportAccount, (state) => ({
    ...state,
    selectedAccount: {
      ...state.selectedAccount,
      accountIsExporting: true
    }
  })),

  on(accountPageAPIActions.exportAccountSuccess, accountPageAPIActions.exportAccountFailure, (state) => ({
    ...state,
    selectedAccount: {
      ...state.selectedAccount,
      accountIsExporting: false
    }
  })),

  on(accountPageActions.updateAccountStatus, (state) => ({
    ...state,
    selectedAccount: {
      ...state.selectedAccount,
      accountStatusUpdating: true
    }
  })),

  on(accountPageAPIActions.changeStatusAccountSuccess, accountPageAPIActions.changeStatusAccountFailure, (state) => ({
    ...state,
    selectedAccount: {
      ...state.selectedAccount,
      accountStatusUpdating: false
    }
  })),

  on(teacherPageActions.initTeacherPage, (state, { accountId }) => ({
    ...state,
    selectedAccount: {
      ...state.selectedAccount,
      accountId: accountId,
      teachers: {
        ...initialState.selectedAccount.teachers,
        loading: true
      }
    }
  })),

  on(teacherApiActions.loadAccountTeachersSuccess, (state, { teachers }) => ({
    ...state,
    selectedAccount: {
      ...state.selectedAccount,
      teachers: {
        ...state.selectedAccount.teachers,
        canCreateClassroomUser: teachers.canCreateClassroomUser,
        canCreateUdTrainee: teachers.canCreateUdTrainee,
        loading: false,
        teachers: teachers.accountUsers,
        filteredTeachers: teachers.accountUsers.filter((teacher) =>
          teacher.roles.some((role) => state.selectedAccount.teachers.filter.includes(role))
        )
      }
    }
  })),

  on(teacherApiActions.loadAccountTeachersFailure, (state) => ({
    ...state,
    selectedAccount: {
      ...state.selectedAccount,
      loading: false
    }
  })),

  on(teacherPageActions.userSearchedTeachers, (state) => ({
    ...state,
    selectedAccount: {
      ...state.selectedAccount,
      teachers: {
        ...state.selectedAccount.teachers,
        loading: true
      }
    }
  })),

  on(teacherApiActions.searchAccountTeachersSuccess, (state, { teachers }) => ({
    ...state,
    selectedAccount: {
      ...state.selectedAccount,
      teachers: {
        ...state.selectedAccount.teachers,
        canCreateClassroomUser: teachers.canCreateClassroomUser,
        canCreateUdTrainee: teachers.canCreateUdTrainee,
        loading: false,
        filteredTeachers: teachers.accountUsers.filter((teacher) =>
          teacher.roles.some((role) => state.selectedAccount.teachers.filter.includes(role))
        )
      }
    }
  })),

  on(teacherPageActions.userUpdatedTeacherTableSort, (state, { sort }) => ({
    ...state,
    selectedAccount: {
      ...state.selectedAccount,
      teachers: {
        ...state.selectedAccount.teachers,
        sort: sort,
        filteredTeachers: [...state.selectedAccount.teachers.filteredTeachers].sort((a, b) => {
          const isAsc = sort.direction === 'asc';
          switch (sort.active) {
            case 'teacherName':
              return compare(a.lastName, b.lastName, isAsc);
            case 'email':
              return compare(a.email, b.email, isAsc);
            case 'role':
              return compare(a.roles.join(','), b.roles.join(','), isAsc);
            default:
              return 0;
          }
        })
      }
    }
  })),

  on(teacherPageActions.userSelectedTeacherRole, (state, { roleType }) => ({
    ...state,
    selectedAccount: {
      ...state.selectedAccount,
      teachers: {
        ...state.selectedAccount.teachers,
        filter: roleType,
        filteredTeachers: state.selectedAccount.teachers.teachers.filter((teacher) =>
          teacher.roles.some((role) => roleType.includes(role))
        )
      }
    }
  })),

  on(studentsPageActions.initStudentsPage, (state, { accountId }) => ({
    ...state,
    selectedAccount: {
      ...state.selectedAccount,
      accountId: accountId,
      students: {
        ...initialState.selectedAccount.students,
        loading: true
      }
    }
  })),

  on(studentsPageAPIActions.loadAccountStudentsSuccess, (state, { students }) => {
    return {
      ...state,
      selectedAccount: {
        ...state.selectedAccount,
        students: {
          ...state.selectedAccount.students,
          pageNumber: students.pageNumber,
          moreStudentsToLoad: students.moreToCome,
          students: students.pageData,
          filteredStudents: students.pageData,
          loading: false
        }
      }
    };
  }),

  on(studentsPageAPIActions.loadStudentGroupsSuccess, (state, { studentGroups }) => {
    return {
      ...state,
      selectedAccount: {
        ...state.selectedAccount,
        students: {
          ...state.selectedAccount.students,
          groups: studentGroups
        }
      }
    };
  }),

  on(studentsPageAPIActions.loadAccountStudentsFailure, (state) => ({
    ...state,
    selectedAccount: {
      ...state.selectedAccount,
      students: {
        ...initialState.selectedAccount.students,
        loading: false
      }
    }
  })),

  on(studentsPageActions.sortStudents, (state, { sort }) => ({
    ...state,
    selectedAccount: {
      ...state.selectedAccount,
      students: {
        ...state.selectedAccount.students,
        sort: sort,
        filteredStudents: [...state.selectedAccount.students.filteredStudents].sort((a, b) => {
          const isAsc = sort.direction === 'asc';
          switch (sort.active) {
            case 'studentName':
              return compare(a.fullName, b.fullName, isAsc);
            case 'group':
              return compare(a.groupName, b.groupName, isAsc);
            case 'schoolYear':
              return compare(a.schoolYearName, b.schoolYearName, isAsc);
            default:
              return 0;
          }
        })
      }
    }
  })),

  on(studentsPageActions.userSyncedAllAccountsStudentLicenses, (state) => ({
    ...state,
    selectedAccount: {
      ...state.selectedAccount,
      students: {
        ...state.selectedAccount.students,
        loading: true
      }
    }
  })),

  on(accountUsagePageActions.initAccountUsagePage, (state, { accountId }) => ({
    ...state,
    selectedAccount: {
      ...state.selectedAccount,
      accountId: accountId,
      usage: {
        ...initialState.selectedAccount.usage,
        loading: true
      }
    }
  })),

  on(accountUsagePageAPIActions.loadAccountUsageSuccess, (state, { usageData }) => ({
    ...state,
    selectedAccount: {
      ...state.selectedAccount,
      usage: {
        ...state.selectedAccount.usage,
        loading: false,
        ...usageData
      }
    }
  })),

  on(accountUsagePageActions.applyDateFilter, accountUsagePageActions.resetDateFilter, (state) => ({
    ...state,
    selectedAccount: {
      ...state.selectedAccount,
      usage: {
        ...initialState.selectedAccount.usage,
        loading: true
      }
    }
  }))
);

function compare(a: number | string, b: number | string, isAsc: boolean) {
  return (a < b ? -1 : 1) * (isAsc ? 1 : -1);
}

function sortSubscriptions(subs: Subscription[], sort: Sort): Subscription[] {
  const isAsc = sort.direction === 'asc';
  return [...subs].sort((a: Subscription, b: Subscription) => {
    switch (sort.active) {
      case 'applicationType':
        return compare(a.applicationTypeId, b.applicationTypeId, isAsc);
      case 'subscriptionStatus':
        return compare(a.subscriptionStatusId, b.subscriptionStatusId, isAsc);
      case 'subscriptionType':
        return compare(a.subscriptionTypeId, b.subscriptionTypeId, isAsc);
      case 'startDate': {
        const dateA = parseDDMMYYYY(a.subscriptionStartDate);
        const dateB = parseDDMMYYYY(b.subscriptionStartDate);
        return compare(dateA?.getTime() ?? 0, dateB?.getTime() ?? 0, isAsc);
      }
      case 'endDate': {
        const dateA = parseDDMMYYYY(a.subscriptionExpiryDate);
        const dateB = parseDDMMYYYY(b.subscriptionExpiryDate);
        return compare(dateA?.getTime() ?? 0, dateB?.getTime() ?? 0, isAsc);
      }
      case 'licences':
        return compare(a.licensesRemaining, b.licensesRemaining, isAsc);
      case 'maxLicences':
        return compare(a.maxLicenseCount, b.maxLicenseCount, isAsc);
      default:
        return 0;
    }
  });

  function parseDDMMYYYY(dateStr: string): Date | null {
    if (!dateStr) return null;
    const [day, month, year] = dateStr.split('/').map(Number);
    const parsedDate = new Date(year, month - 1, day);
    return isNaN(parsedDate.getTime()) ? null : parsedDate;
  }
}
